import { CheckCircleRounded, CloseRounded, ErrorRounded, InfoRounded, WarningRounded } from "@mui/icons-material";
import { Button, styled } from "@mui/material";
import {
  SnackbarProvider as DefaultSnackbarProvider,
  MaterialDesignContent,
  SnackbarKey,
  useSnackbar,
} from "notistack";

interface SnackbarCloseButtonProps {
  id: SnackbarKey;
}

const SnackbarCloseButton = (props: SnackbarCloseButtonProps) => {
  const { closeSnackbar } = useSnackbar();

  return (
    <Button onClick={() => closeSnackbar(props.id)} sx={{ minWidth: "24px" }}>
      <CloseRounded />
    </Button>
  );
};

const DefaultSnackbar = styled(MaterialDesignContent)`
  .MuiSvgIcon-root {
    color: ${({ theme }) => theme.palette.common.black};
    font-size: 24px;
  }

  #notistack-snackbar {
    overflow: hidden !important;
  }

  &.notistack-MuiContent {
    background-color: transparent;
    box-shadow: none;
    border-radius: 8px;
    color: ${({ theme }) => theme.palette.common.black};
    padding-left: ${({ theme }) => theme.spacing(0.5)};
    padding-right: ${({ theme }) => theme.spacing(0.5)};
    padding-top: ${({ theme }) => theme.spacing(0.25)};
    padding-bottom: ${({ theme }) => theme.spacing(0.25)};
    min-width: min(800px, 75vw);
    font-size: 1rem;
    line-height: 1.5rem;
    font-weight: 700;
    text-transform: none;
  }

  .notistack-Icon {
    margin-right: ${({ theme }) => theme.spacing(0.5)};
  }

  &.notistack-MuiContent-error {
    background-color: #fed7d7;

    .notistack-Icon,
    .MuiTouchRipple-root {
      color: #c53030;
    }
  }

  &.notistack-MuiContent-default,
  &.notistack-MuiContent-info {
    background-color: #ceedff;

    .notistack-Icon,
    .MuiTouchRipple-root {
      color: #2a69ac;
    }
  }

  &.notistack-MuiContent-success {
    background-color: #c6f6d5;

    .notistack-Icon,
    .MuiTouchRipple-root {
      color: #2f855a;
    }
  }

  &.notistack-MuiContent-warning {
    background-color: #feebc8;

    .notistack-Icon,
    .MuiTouchRipple-root {
      color: #ed8936;
    }
  }
`;

interface IProps {
  children?: React.ReactNode;
}

const SnackbarProvider = (props: IProps) => {
  return (
    <DefaultSnackbarProvider
      action={(id) => <SnackbarCloseButton id={id} />}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      iconVariant={{
        default: <InfoRounded className="notistack-Icon" />,
        error: <ErrorRounded className="notistack-Icon" />,
        info: <InfoRounded className="notistack-Icon" />,
        success: <CheckCircleRounded className="notistack-Icon" />,
        warning: <WarningRounded className="notistack-Icon" />,
      }}
      autoHideDuration={3000}
      variant="info"
      preventDuplicate={true}
      Components={{
        default: DefaultSnackbar,
        error: DefaultSnackbar,
        info: DefaultSnackbar,
        success: DefaultSnackbar,
        warning: DefaultSnackbar,
      }}
      maxSnack={10}
      style={{ maxWidth: "1400px", flexWrap: "nowrap"}}
    >
      {props.children}
    </DefaultSnackbarProvider>
  );
};

export { SnackbarProvider };
